import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommentIcon from '@mui/icons-material/Comment';

const JobStatusBadge = ({ status, job }) => {
  let color = '';
  let label = '';
  
  // Determine status based on job_verified and job_review_remark
  if (job && job.job_verified === 1) {
    color = 'success';
    label = 'Valid';
  } else if (job && job.job_review_remark && job.job_review_remark.trim() !== '') {
    color = 'warning';
    label = 'Remarked';
  } else {
    color = 'default';
    label = 'Pending';
  }
  
  return (
    <Chip 
      label={label} 
      color={color} 
      size="small"
      variant="outlined"
    />
  );
};

JobStatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  job: PropTypes.object.isRequired
};

const JobTable = ({ jobs, onViewDetails, onMarkValid, onAddRemark }) => {
  const [openRemarkDialog, setOpenRemarkDialog] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState('');
  
  const handleRemarkClick = (remark) => {
    setSelectedRemark(remark);
    setOpenRemarkDialog(true);
  };
  
  const handleCloseRemarkDialog = () => {
    setOpenRemarkDialog(false);
  };
  
  if (!jobs || jobs.length === 0) {
    return (
      <Paper sx={{ p: 5, mb: 4 }}>
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No jobs found. Try adjusting your search filters or switching to a different tab.
          </Typography>
        </Box>
      </Paper>
    );
  }
  
  // Only display the jobs for the current page, which are provided by the context
  
  return (
    <>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Job Title</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>State</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Skills</TableCell>
              <TableCell>Date Added</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                key={job.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  <Typography variant="body2">{job.title}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{job.company}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {job.location.state || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {job.location.city || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {job.skills.slice(0, 3).map((skill, index) => (
                      <Chip key={index} label={skill} size="small" variant="outlined" />
                    ))}
                    {job.skills.length > 3 && (
                      <Chip 
                        label={`+${job.skills.length - 3}`} 
                        size="small" 
                        variant="outlined" 
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{job.date}</Typography>
                </TableCell>
                <TableCell>
                  <JobStatusBadge status={job.status} job={job} />
                </TableCell>
                <TableCell>
                  {job.job_review_remark && job.job_review_remark.trim() !== '' ? (
                    <Tooltip title="Click to view full remark" placement="top">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => handleRemarkClick(job.job_review_remark)}
                      >
                        {job.job_review_remark}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No remarks
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => onViewDetails(job)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={job.job_verified === 1 ? "Already Validated" : "Mark as Valid"}>
                      <IconButton 
                        size="small" 
                        color={job.job_verified === 1 ? 'success' : 'default'}
                        onClick={() => onMarkValid(job.id)}
                        disabled={job.job_verified === 1}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={job.job_review_remark && job.job_review_remark !== '' ? "Update Remark" : "Add Remark"}>
                      <IconButton 
                        size="small"
                        color={job.job_review_remark && job.job_review_remark !== '' ? 'warning' : 'secondary'}
                        onClick={() => onAddRemark(job.id, job.job_review_remark)}
                      >
                        <CommentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Dialog to show full remark text */}
      <Dialog 
        open={openRemarkDialog} 
        onClose={handleCloseRemarkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Job Remark</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
            {selectedRemark}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemarkDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

JobTable.propTypes = {
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      company: PropTypes.string.isRequired,
      location: PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string
      }),      skills: PropTypes.arrayOf(PropTypes.string),
      date: PropTypes.string,
      description: PropTypes.string,
      status: PropTypes.string,
      job_verified: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      job_review_remark: PropTypes.string
    })
  ).isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onMarkValid: PropTypes.func.isRequired,
  onAddRemark: PropTypes.func.isRequired
};

export default JobTable;
